import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';

// app
import { LayoutView } from './Layout.view';
import { expandSidebar, collapseSidebar } from 'stores';
import { useMedia } from 'hooks';
import * as constants from 'consts';

// context
export const LayoutContext = React.createContext({});

Layout.propTypes = {
  main: PropTypes.bool,
  sidebar: PropTypes.bool,
  isCentered: PropTypes.bool,
  testid: PropTypes.string,
  padding: PropTypes.bool,
};

export default function Layout({ main, sidebar, isCentered, testid, children }) {
  const dispatch = useDispatch();
  const media = useMedia();
  const refMain = useRef(null);
  const refSidebar = useRef(null);
  const uiSidebarExpanded = useSelector((state) => get(state, 'ui.sidebar.expanded'));
  const uiModalVisible = useSelector((state) => get(state, 'ui.modal.visible'));
  const uiModalType = useSelector((state) => get(state, 'ui.modal.type'));

  useEffect(
    () => {
      document.addEventListener('keydown', handleEscape);

      // cleanup
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    },
    [uiSidebarExpanded] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSidebarClick = (forcedStatus) => (event) => {
    const isCollapsed = forcedStatus === 'collapsed' ? true : uiSidebarExpanded;

    if (isCollapsed) {
      dispatch(collapseSidebar());
    } else {
      dispatch(expandSidebar());
    }
  };

  const handleEscape = (event) => {
    if (uiSidebarExpanded && event.keyCode === constants.KEYCODE.Escape) {
      event.preventDefault();
      dispatch(collapseSidebar());
    }
  };

  // abort
  if (main || sidebar) return children;

  const childrenArray = children ? (Array.isArray(children) ? children : [children]) : [];
  const childrenMain = childrenArray.filter((c) => c.props && c.props.main);
  const childrenSidebar = childrenArray.filter((c) => c.props && c.props.sidebar);

  return (
    <LayoutContext.Provider
      value={{
        refMain: refMain,
        refSidebar: refSidebar,
      }}
    >
      <LayoutView
        main={main}
        sidebar={sidebar}
        isCentered={isCentered}
        mobile={media.mobile}
        tablet={media.tablet}
        collapsed={!uiSidebarExpanded}
        hasSidebar={childrenSidebar && childrenSidebar.length > 0}
        hasModal={Boolean(uiModalVisible && uiModalType)}
        childrenMain={childrenMain}
        childrenSidebar={childrenSidebar}
        testid={testid}
        handleSidebarClick={handleSidebarClick}
      />
    </LayoutContext.Provider>
  );
}
